import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import Modal from "@/components/Modal";
import { useForm, useWatch } from "react-hook-form";
import FormCurrencyInput from "@/components/FormCurrencyInput";
import { updateIncentivePaymentReq } from "@/api/projectsApi";
import { ProjectRecord } from "@/types";
import SVGEditNote from "@/assets/icons/SVGEditNote";
import { StyledIconButton } from "@/components/styled";

type Props = {
  projectId: ProjectRecord["id"];
  currentIncentive: number; // in cents
  onSuccess?: () => void;
  iconOnly?: boolean;
};

type FormData = {
  upfrontIncentivePayment: number;
};

const EditIncentiveModal = ({
  projectId,
  currentIncentive,
  onSuccess,
  iconOnly = false,
}: Props) => {
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      upfrontIncentivePayment: currentIncentive / 100, // Convert cents to dollars
    },
  });

  const watchedValue = useWatch({
    control,
    name: "upfrontIncentivePayment",
    defaultValue: currentIncentive / 100,
  });

  const handleOpen = () => {
    reset({
      upfrontIncentivePayment: currentIncentive / 100,
    });
    setOpen(true);
    setError(null);
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setOpen(false);
      setError(null);
      reset();
    }
  };

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Convert dollars to cents for API
      const incentiveInCents = Math.round(data.upfrontIncentivePayment * 100);

      await updateIncentivePaymentReq(projectId, incentiveInCents);

      setOpen(false);
      if (onSuccess) {
        onSuccess();
      }
    } catch (err: any) {
      setError(
        err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to update incentive payment. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      {iconOnly ? (
        <StyledIconButton onClick={handleOpen}>
          <SVGEditNote />
        </StyledIconButton>
      ) : (
        <Button
          variant="outlined"
          onClick={handleOpen}
          sx={{
            padding: "6px 12px",
            fontSize: "14px",
            color: "#0076CE",
            borderColor: "#0076CE",
            borderRadius: "8px",
            "&:hover": {
              backgroundColor: "rgba(0, 118, 206, 0.04)",
              borderColor: "#0076CE",
            },
          }}
        >
          Edit Incentive
        </Button>
      )}

      <Modal
        open={open}
        onClose={handleClose}
        title="Edit Upfront Incentive Payment"
        boxSx={{
          maxWidth: 500,
          width: "100%",
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box sx={{ mt: 3, mb: 3 }}>
            <Typography variant="body2" sx={{ mb: 2, color: "textLightGray3" }}>
              Update the upfront incentive payment amount for this project.
            </Typography>

            <FormCurrencyInput
              name="upfrontIncentivePayment"
              control={control}
              label="Upfront Incentive Amount"
              placeholder="Enter amount"
              value={
                watchedValue !== undefined
                  ? `$${Number(watchedValue).toFixed(2)}`
                  : undefined
              }
              controlProps={{
                rules: {
                  required: "Incentive amount is required",
                  validate: {
                    positive: (value) =>
                      value > 0 || "Amount must be greater than 0",
                    isNumber: (value) =>
                      !isNaN(value) || "Please enter a valid number",
                    maxValue: (value) =>
                      value <= 1000000 || "Amount cannot exceed $1,000,000",
                  },
                },
              }}
            />

            {errors.upfrontIncentivePayment && (
              <Typography
                variant="body2"
                sx={{ color: "error.main", mt: 1, fontSize: "12px" }}
              >
                {errors.upfrontIncentivePayment.message}
              </Typography>
            )}

            {error && (
              <Typography
                variant="body2"
                sx={{ color: "error.main", mt: 2, fontSize: "14px" }}
              >
                {error}
              </Typography>
            )}
          </Box>

          <Box
            sx={{
              display: "flex",
              gap: 2,
              justifyContent: "flex-end",
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isSubmitting}
              sx={{
                borderColor: "#D0D5DD",
                color: "#344054",
                "&:hover": {
                  borderColor: "#D0D5DD",
                  backgroundColor: "rgba(0, 0, 0, 0.04)",
                },
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={{
                backgroundColor: "#0076CE",
                color: "#fff",
                "&:hover": {
                  backgroundColor: "#0056a3",
                },
                "&:disabled": {
                  backgroundColor: "#B0B0B0",
                  color: "#fff",
                },
              }}
            >
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </Box>
        </form>
      </Modal>
    </>
  );
};

export default EditIncentiveModal;
